import React, { memo, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, InputAdornment, TextField } from '@mui/material';
import ControlledError from '../ControlledError';

import { ReactComponent as ArrowDown } from '../../../../assets/icons/ArrowDown.svg';

import { useStyles } from './styles';
import { useStyles as textfieldStyles } from '../TextField/styles';
import { useTranslation } from 'react-i18next';

interface ISelectProps {
  error?: string;
  inputLabel?: string;
  required?: boolean;
  isClearable?: boolean;
  options: { value: any; label: string; group?: string; [key: string]: any }[];
  selectedValue: any;
  disabled?: boolean;
  loading?: boolean;
  skippable?: boolean;
  valueAsSelectedValue?: boolean;
  grouped?: boolean;
  /** Base id for automation. The Autocomplete root uses `${testId}`, each option
   * `${testId}-option-{value}` (`${testId}-option-add` for the "add new" entry). */
  testId?: string;
  onChange: (selectedValue: any) => void;
  onBlur?: (e?: any) => void;
  addOption?: boolean;
}

const Select = ({
  error,
  inputLabel,
  required,
  isClearable,
  options,
  selectedValue,
  disabled,
  loading = false,
  skippable = false,
  valueAsSelectedValue,
  grouped = false,
  testId,
  onChange,
  onBlur,
  addOption = false,
}: ISelectProps) => {
  const classes = useStyles();
  const textFieldClasses = textfieldStyles();
  const [inputValue, setInputValue] = useState('');
  const { t } = useTranslation();

  // Create dynamic options that include "Add [text]" option when addOption is true
  const dynamicOptions = useMemo(() => {
    if (!addOption || !inputValue.trim()) {
      return options;
    }

    // Check if the input value already exists in options
    const exactMatch = options.some(option => option.label.toLowerCase() === inputValue.toLowerCase());

    if (exactMatch) {
      return options;
    }

    // Add the "Add [text]" option at the beginning
    const addNewOption = {
      value: `__ADD__${inputValue}`,
      label: t('common.addInput').replace('$INPUT_VALUE', `"${inputValue}"`),
      isAddOption: true,
    };

    return [addNewOption, ...options];
  }, [options, inputValue, addOption, t]);

  const handleInputChange = (event, value, reason) => {
    if (reason !== 'reset') {
      setInputValue(value);
    }
  };

  const handleChange = (event, newValue) => {
    // Check if the selected option is an "Add [text]" option
    if (newValue && newValue.isAddOption) {
      const newText = newValue.value.replace('__ADD__', '');
      const newOption = {
        value: newText,
        label: newText,
        isNewAdded: true,
      };
      onChange(newOption);
    } else {
      onChange(valueAsSelectedValue ? newValue.value : newValue);
    }

    // Clear input after selection
    setInputValue('');
  };

  const isOptionEqualToValue = (option, value) => {
    if (!option || !value) return false;

    // Both should be objects with value property
    if (
      typeof option === 'object' &&
      typeof value === 'object' &&
      option.value !== undefined &&
      value.value !== undefined
    ) {
      return option.value === value.value;
    }

    return false;
  };

  return (
    <ControlledError show={!!error} error={error}>
      <Autocomplete
        data-testid={testId}
        // Always resolve to an option object or null (never undefined) so MUI keeps the
        // Autocomplete controlled. Returning undefined on the first render latches it as
        // uncontrolled for its lifetime, which breaks prefilled values (e.g. edit modals).
        value={
          valueAsSelectedValue
            ? options.find(option => option.value === selectedValue) ?? null
            : selectedValue ?? null
        }
        options={dynamicOptions}
        groupBy={grouped ? option => (option as any).group || '' : undefined}
        // clearIcon={null}
        inputValue={addOption ? inputValue : undefined}
        onInputChange={addOption ? handleInputChange : undefined}
        // multiple={isMulti}
        disabled={disabled}
        className={classes.autocomplete}
        classes={{
          option: classes.option,
          paper: classes.autocomplete,
        }}
        getOptionLabel={option => (option ? option.label || '' : '')}
        isOptionEqualToValue={isOptionEqualToValue}
        onChange={handleChange}
        onBlur={onBlur}
        renderGroup={
          grouped
            ? params => (
                <div>
                  <div className={classes.groupHeader}>{params.group}</div>
                  <div className={classes.groupOptions}>{params.children}</div>
                </div>
              )
            : undefined
        }
        renderInput={params => (
          <TextField
            {...params}
            error={!!error}
            label={
              <>
                <span>{inputLabel}</span>
                {(required || skippable) && (
                  <span className={required ? textFieldClasses.requiredMark : textFieldClasses.skippableMark}>
                    {required ? '*' : '**'}
                  </span>
                )}
              </>
            }
            className={textFieldClasses.input}
            InputProps={{
              ...(params.InputProps || {}),
              endAdornment: loading ? (
                <InputAdornment position="end" className={classes.dropdownIcon}>
                  <CircularProgress size={18} color="inherit" />
                </InputAdornment>
              ) : (
                <InputAdornment
                  position="end"
                  className={`${classes.dropdownIcon} ${
                    params?.inputProps?.['aria-expanded'] ? classes.dropdownIconRotated : ''
                  }`}
                  onClick={
                    params?.inputProps?.['aria-expanded']
                      ? () => setTimeout(() => (params.inputProps?.ref as any)?.current?.blur?.(), 0)
                      : (params.inputProps as any)?.onMouseDown
                  }
                >
                  <ArrowDown />
                </InputAdornment>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li
            {...props}
            data-testid={testId ? `${testId}-option-${option.isAddOption ? 'add' : option.value}` : undefined}
            className={`${props.className || ''} ${classes.option} ${
              props['aria-selected'] ? classes.selectedOption : ''
            } ${option.isAddOption ? classes.addOption : ''}`}
          >
            {option.label}
          </li>
        )}
        disableClearable={!isClearable}
      />
    </ControlledError>
  );
};

export default memo(Select);
